// components/tutorial/tutorial-series-filters.tsx
// Simplified filters for series view: search + tags (no difficulty)
"use client";

import { memo, useCallback, useState, useTransition } from "react";

import { Badge } from "@vllnt/ui";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

export type TutorialSeriesFiltersProps = {
  currentTags: string[];
  searchQuery: string;
  tags: string[];
};

type FilterUpdates = { search?: string; tags?: string[] };

function useFilterUpdater(): [(updates: FilterUpdates) => void, boolean] {
  const router = useRouter();
  const pathname = usePathname();
  // oxlint-disable-next-line react-doctor/nextjs-no-use-search-params-without-suspense -- parent layout Suspense wraps this consumer
  const searchParameters = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const updateParameters = useCallback(
    (updates: FilterUpdates) => {
      const parameters = new URLSearchParams(searchParameters.toString());

      if (updates.search !== undefined) {
        if (updates.search === "") {
          parameters.delete("search");
        } else {
          parameters.set("search", updates.search);
        }
      }

      if (updates.tags !== undefined) {
        if (updates.tags.length === 0) {
          parameters.delete("tags");
        } else {
          parameters.set("tags", updates.tags.join(","));
        }
      }

      const queryString = parameters.toString();
      const url = queryString ? `${pathname}?${queryString}` : pathname;

      startTransition(() => {
        router.push(url);
      });
    },
    [pathname, router, searchParameters],
  );

  return [updateParameters, isPending];
}

type SearchInputProps = {
  isPending: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchQuery: string;
};

function SearchInput({
  isPending,
  onChange,
  searchQuery,
}: SearchInputProps): React.ReactNode {
  const t = useTranslations("tutorials");

  return (
    <div>
      <label className="sr-only" htmlFor="series-search">
        {t("search_label")}
      </label>
      <input
        className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        defaultValue={searchQuery}
        disabled={isPending}
        id="series-search"
        onChange={onChange}
        placeholder={t("search_placeholder")}
        type="text"
      />
    </div>
  );
}

type TagFilterProps = {
  currentTags: string[];
  onClearTags: () => void;
  onTagToggle: (tag: string) => void;
  tags: string[];
};

const MAX_VISIBLE_TAGS_DESKTOP = 10;
const MAX_VISIBLE_TAGS_MOBILE = 5;

function OverflowBadges({
  desktopCount,
  expanded,
  mobileCount,
  onToggle,
  showDesktop,
  showMobile,
}: {
  desktopCount: number;
  expanded: boolean;
  mobileCount: number;
  onToggle: () => void;
  showDesktop: boolean;
  showMobile: boolean;
}): React.ReactNode {
  if (expanded && showMobile) {
    return (
      <Badge
        className="cursor-pointer hover:bg-muted transition-colors"
        onClick={onToggle}
        variant="outline"
      >
        −
      </Badge>
    );
  }

  return (
    <>
      {!expanded && showMobile ? (
        <Badge
          className="cursor-pointer hover:bg-muted transition-colors sm:hidden"
          onClick={onToggle}
          variant="outline"
        >
          {`+${mobileCount}`}
        </Badge>
      ) : undefined}
      {!expanded && showDesktop ? (
        <Badge
          className="cursor-pointer hover:bg-muted transition-colors hidden sm:inline-flex"
          onClick={onToggle}
          variant="outline"
        >
          {`+${desktopCount}`}
        </Badge>
      ) : undefined}
    </>
  );
}

function TagFilter({
  currentTags,
  onClearTags,
  onTagToggle,
  tags,
}: TagFilterProps): React.ReactNode {
  const t = useTranslations("tutorials");
  const [expanded, setExpanded] = useState(false);
  const handleExpand = useCallback(() => {
    setExpanded((previous) => !previous);
  }, []);

  if (tags.length === 0) return null;

  const visibleTags = expanded ? tags : tags.slice(0, MAX_VISIBLE_TAGS_DESKTOP);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium">{t("tags_label")}</span>
        {currentTags.length > 0 ? (
          <button
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClearTags}
            type="button"
          >
            {t("clear")}
          </button>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {visibleTags.map((tag, index) => {
          const isActive = currentTags.includes(tag);
          const desktopOnly = !expanded && index >= MAX_VISIBLE_TAGS_MOBILE;
          return (
            <Badge
              className={`cursor-pointer transition-all ${
                isActive
                  ? "bg-primary text-primary-foreground border-transparent"
                  : "hover:bg-muted"
              }${desktopOnly ? " hidden sm:inline-flex" : ""}`}
              key={tag}
              onClick={() => {
                onTagToggle(tag);
              }}
              variant={isActive ? "default" : "outline"}
            >
              {tag}
            </Badge>
          );
        })}
        <OverflowBadges
          desktopCount={tags.length - MAX_VISIBLE_TAGS_DESKTOP}
          expanded={expanded}
          mobileCount={tags.length - MAX_VISIBLE_TAGS_MOBILE}
          onToggle={handleExpand}
          showDesktop={tags.length > MAX_VISIBLE_TAGS_DESKTOP}
          showMobile={tags.length > MAX_VISIBLE_TAGS_MOBILE}
        />
      </div>
    </div>
  );
}

type ActiveFiltersSummaryProps = {
  currentTags: string[];
  onClearAll: () => void;
  searchQuery: string;
};

function ActiveFiltersSummary({
  currentTags,
  onClearAll,
  searchQuery,
}: ActiveFiltersSummaryProps): React.ReactNode {
  const t = useTranslations("tutorials");

  if (!searchQuery && currentTags.length === 0) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span>{t("active_filters")}</span>
      {currentTags.map((tag) => (
        <Badge key={tag} variant="secondary">
          {tag}
        </Badge>
      ))}
      {searchQuery ? (
        <Badge variant="secondary">
          {t("search_filter")} &quot;{searchQuery}&quot;
        </Badge>
      ) : null}
      <button
        className="text-xs hover:underline"
        onClick={onClearAll}
        type="button"
      >
        {t("clear_all")}
      </button>
    </div>
  );
}

function TutorialSeriesFiltersImpl({
  currentTags,
  searchQuery,
  tags,
}: TutorialSeriesFiltersProps): React.ReactNode {
  const [updateParameters, isPending] = useFilterUpdater();

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateParameters({ search: event.target.value });
    },
    [updateParameters],
  );

  const handleTagToggle = useCallback(
    (tag: string) => {
      const newTags = currentTags.includes(tag)
        ? currentTags.filter((t) => t !== tag)
        : [...currentTags, tag];
      updateParameters({ tags: newTags });
    },
    [currentTags, updateParameters],
  );

  const handleClearTags = useCallback(() => {
    updateParameters({ tags: [] });
  }, [updateParameters]);

  const handleClearAll = useCallback(() => {
    updateParameters({ search: "", tags: [] });
    const input = document.querySelector<HTMLInputElement>("#series-search");
    if (input) input.value = "";
  }, [updateParameters]);

  return (
    <div className="space-y-4 mb-8">
      <SearchInput
        isPending={isPending}
        onChange={handleSearchChange}
        searchQuery={searchQuery}
      />
      <TagFilter
        currentTags={currentTags}
        onClearTags={handleClearTags}
        onTagToggle={handleTagToggle}
        tags={tags}
      />
      <ActiveFiltersSummary
        currentTags={currentTags}
        onClearAll={handleClearAll}
        searchQuery={searchQuery}
      />
    </div>
  );
}

export const TutorialSeriesFilters = memo(TutorialSeriesFiltersImpl);
TutorialSeriesFilters.displayName = "TutorialSeriesFilters";
